import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

export interface ScoringProgress {
  status: 'in_progress' | 'completed' | 'failed';
  current: number;
  total: number;
  message: string;
  startedAt: string;
  completedAt?: string;
  error?: string;
}

interface ScoringProgressModalProps {
  open: boolean;
  progress: ScoringProgress | null;
  onComplete?: () => void;
}

export const ScoringProgressModal: React.FC<ScoringProgressModalProps> = ({
  open,
  progress,
  onComplete,
}) => {
  // Calculate percentage
  const percentage = progress ? Math.round((progress.current / progress.total) * 100) : 0;

  // Time estimation state
  const [countdown, setCountdown] = React.useState<number | null>(null);
  const lastProgressRef = React.useRef<number>(0);
  const lastUpdateTimeRef = React.useRef<number>(Date.now());
  const batchTimesRef = React.useRef<number[]>([]);

  // Calculate time estimation after first batch
  React.useEffect(() => {
    if (!progress || progress.status !== 'in_progress') {
      setCountdown(null);
      batchTimesRef.current = [];
      lastProgressRef.current = 0;
      lastUpdateTimeRef.current = Date.now();
      return;
    }

    // If progress has updated
    if (progress.current > lastProgressRef.current && lastProgressRef.current > 0) {
      const now = Date.now();
      const timeSinceLastUpdate = (now - lastUpdateTimeRef.current) / 1000; // seconds
      const progressDelta = progress.current - lastProgressRef.current;
      const timePerEntry = timeSinceLastUpdate / progressDelta;

      // Track batch times (keep last 5 for moving average)
      const newTimes = [...batchTimesRef.current, timePerEntry].slice(-5);
      batchTimesRef.current = newTimes;

      // Calculate average time per entry from recent batches
      const avgTimePerEntry =
        newTimes.length > 0
          ? newTimes.reduce((sum, time) => sum + time, 0) / newTimes.length
          : timePerEntry;

      // Calculate remaining entries and estimated time
      const remainingEntries = progress.total - progress.current;
      const estimatedSeconds = Math.ceil(remainingEntries * avgTimePerEntry);

      setCountdown(estimatedSeconds);
      lastUpdateTimeRef.current = now;
    }

    lastProgressRef.current = progress.current;
  }, [progress?.current, progress?.total, progress?.status]);

  // Countdown timer between updates
  React.useEffect(() => {
    if (countdown === null || countdown <= 0 || progress?.status !== 'in_progress') {
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => (prev !== null && prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown, progress?.status]);

  // Format time remaining display
  const formatTimeRemaining = (seconds: number | null): string => {
    if (seconds === null) return '';
    if (seconds < 60) return `~${seconds}s remaining`;

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) {
      return remainingSeconds > 0
        ? `~${minutes}m ${remainingSeconds}s remaining`
        : `~${minutes}m remaining`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `~${hours}h ${remainingMinutes}m remaining`
      : `~${hours}h remaining`;
  };

  // Auto-close after 2 seconds on success
  React.useEffect(() => {
    if (progress?.status === 'completed' && onComplete) {
      const timer = setTimeout(() => {
        onComplete();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [progress?.status, onComplete]);

  return (
    <Dialog open={open} onOpenChange={() => {}} modal={true}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={e => {
          // Prevent closing while in progress
          if (progress?.status === 'in_progress') {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={e => {
          // Prevent closing with ESC while in progress
          if (progress?.status === 'in_progress') {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {progress?.status === 'in_progress' && (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Scoring in Progress
              </>
            )}
            {progress?.status === 'completed' && (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Scoring Complete
              </>
            )}
            {progress?.status === 'failed' && (
              <>
                <AlertCircle className="h-5 w-5 text-red-600" />
                Scoring Failed
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {progress?.status === 'in_progress' && 'Please wait while we score all entries...'}
            {progress?.status === 'completed' && 'All entries have been scored successfully!'}
            {progress?.status === 'failed' && 'An error occurred during scoring.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Progress Bar */}
          {progress && progress.status !== 'failed' && (
            <div className="space-y-2">
              <Progress value={percentage} className="h-2" />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>
                  {progress.current.toLocaleString()} of {progress.total.toLocaleString()} entries
                </span>
                <span>{percentage}%</span>
              </div>
              {/* Time estimation or ranking message */}
              {progress.current === progress.total && progress.status === 'in_progress' ? (
                <div className="text-center text-sm text-muted-foreground font-medium">
                  Calculating rankings...
                </div>
              ) : countdown !== null && countdown > 0 ? (
                <div className="text-center text-sm text-muted-foreground font-medium">
                  {formatTimeRemaining(countdown)}
                </div>
              ) : null}
            </div>
          )}

          {/* Status Message */}
          {progress?.message && (
            <p className="text-sm text-center text-muted-foreground">{progress.message}</p>
          )}

          {/* Error Message */}
          {progress?.status === 'failed' && progress.error && (
            <Alert variant="destructive">
              <AlertDescription>{progress.error}</AlertDescription>
            </Alert>
          )}

          {/* Info Message */}
          {progress?.status === 'in_progress' && (
            <Alert>
              <AlertDescription className="text-sm">
                Scoring is running in the background. You can navigate away from this page and the
                scoring will continue. This may take several minutes for large pickems.
              </AlertDescription>
            </Alert>
          )}

          {/* Success Message */}
          {progress?.status === 'completed' && (
            <Alert className="border-green-200 bg-green-50">
              <AlertDescription className="text-sm text-green-800">
                Closing in 2 seconds...
              </AlertDescription>
            </Alert>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
